"use client";
import { useMemo, useState, type CSSProperties } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { ProductCard, productCardData } from "./product-card";
import { currency, number } from "@/lib/config";
import type { Catalog } from "@/lib/types";
import { normalizeSearchText } from "@/lib/search-text";
import { groupColorProducts } from "@/lib/product-color-families";
import { currentProductPrice, isProductDiscounted } from "@/lib/product-discounts";
import { productMatchesStorefrontCategory } from "@/lib/storefront-category-tree";
type Filters = {
  q: string;
  category: string;
  color: string;
  material: string;
  availability: string;
  min: string;
  max: string;
  sort: string;
};
const empty: Filters = {
  q: "",
  category: "",
  color: "",
  material: "",
  availability: "",
  min: "",
  max: "",
  sort: "newest",
};
export function CatalogBrowser({
  catalog,
  initial = {},
  collection,
}: {
  catalog: Catalog;
  initial?: Partial<Filters>;
  collection?: "flash-deals";
}) {
  const [f, setF] = useState<Filters>({ ...empty, ...initial }),
    [limit, setLimit] = useState(6),
    [showFilters, setShowFilters] = useState(false);
  const set = (key: keyof Filters, value: string) => {
    setF((prev) => ({ ...prev, [key]: value }));
    setLimit(6);
  };
  const displayProducts = useMemo(() => {
    const visible = catalog.products.filter((product) => {
      const hasImage = product.images.some((image) => image.trim() && !/(?:^|\/)placeholder\.svg(?:[?#]|$)/i.test(image));
      const isDeal = isProductDiscounted(product);
      return hasImage && (!collection || isDeal);
    });
    return groupColorProducts(visible, catalog.products);
  }, [catalog.products, collection]);
  const colors = [
    ...new Set(displayProducts.flatMap((p) => [...(p.availableColors ?? []), ...p.variants.map((v) => v.color)])),
  ];
  const materials = [...new Set(catalog.products.map((p) => p.material).filter(Boolean))];
  const productPrice = (p: Catalog["products"][number]) => {
    return currentProductPrice(p);
  };
  const highestPrice = useMemo(() => {
    let highest = 0;
    for (const product of displayProducts) {
      const offers = product.familyOffers?.length ? product.familyOffers : product.variants;
      if (!offers.length) highest = Math.max(highest, product.basePrice ?? 0);
      for (const offer of offers) {
        if (Number.isFinite(offer.price)) highest = Math.max(highest, offer.price);
      }
    }
    return highest;
  }, [displayProducts]);
  const selectedMin = Math.min(highestPrice, Math.max(0, Number(f.min) || 0));
  const selectedMax = f.max ? Math.min(highestPrice, Math.max(selectedMin, Number(f.max))) : highestPrice;
  const products = displayProducts
    .filter(
      (p) =>
        (!f.q || normalizeSearchText(`${p.name} ${p.description} ${(p.searchTerms ?? []).join(" ")}`).includes(normalizeSearchText(f.q))) &&
        (!f.category || productMatchesStorefrontCategory(p, f.category)) &&
        (!f.material || p.material === f.material) &&
        ((p.familyOffers?.length ? p.familyOffers : p.variants.length ? p.variants : [{ color: "", price: p.basePrice ?? 0, stock: 0, made_to_order: false }])).some(
          (v) =>
            (!f.color || v.color === f.color) &&
            (!f.min || v.price >= Number(f.min)) &&
            (!f.max || v.price <= Number(f.max)) &&
            (!f.availability || (p.variants.length > 0 &&
              (f.availability === "order"
                ? v.made_to_order
                : f.availability === "ready"
                  ? v.stock > 0 && !v.made_to_order
                  : v.stock === 0 && !v.made_to_order))),
        ),
    )
    .sort((a, b) =>
      f.sort === "price-asc"
        ? productPrice(a) - productPrice(b)
        : f.sort === "price-desc"
          ? productPrice(b) - productPrice(a)
            : b.created_at.localeCompare(a.created_at),
    );
  const labels: Record<string, string> = {
    q: "جست‌وجو",
    category: "دسته",
    color: "رنگ",
    material: "جنس",
    availability: "وضعیت",
    min: "از قیمت",
    max: "تا قیمت",
  };
  const display = (key: string, value: string) =>
    key === "category"
      ? catalog.categories.find((c) => c.slug === value)?.name || value
      : key === "availability"
        ? { order: "ساخت سفارشی", ready: "آماده", unavailable: "ناموجود" }[
            value
          ]
        : key === "min" || key === "max"
          ? number(Number(value))
          : value;
  return (
    <div>
      <div className="catalog-toolbar">
        <span>
          {number(products.length)} محصول
        </span>
        <button
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
          aria-expanded={showFilters}
        >
          <SlidersHorizontal size={18} /> فیلترها
        </button>
        <label>
          مرتب‌سازی{" "}
          <select value={f.sort} onChange={(e) => set("sort", e.target.value)}>
            <option value="newest">
              {catalog.demo ? "چیدمان مجموعه" : "جدیدترین"}
            </option>
            {!catalog.demo && <option value="price-asc">کمترین قیمت</option>}
            {!catalog.demo && <option value="price-desc">بیشترین قیمت</option>}
          </select>
        </label>
      </div>
      <div className="catalog-layout">
        <aside
          className={`filters ${showFilters ? "is-open" : ""}`}
          aria-label="فیلتر محصولات"
        >
          <div className="filter-title">
            <h2>فیلتر محصولات</h2>
            <SlidersHorizontal size={19} />
          </div>
          <form className="field" role="search" onSubmit={(event) => event.preventDefault()}>
            <label htmlFor="catalog-search">جست‌وجو</label>
            <div className="input-icon">
              <input id="catalog-search" name="q" value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="نام محصول…" />
              <button type="submit" aria-label="جست‌وجوی محصولات"><Search size={17} /></button>
            </div>
          </form>
          <label className="field">
            دسته‌بندی
            <select
              value={f.category}
              onChange={(e) => set("category", e.target.value)}
            >
              <option value="">همهٔ دسته‌ها</option>
              {catalog.categories.map((c) => (
                <option key={c.id} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            رنگ
            <select
              value={f.color}
              onChange={(e) => set("color", e.target.value)}
            >
              <option value="">همهٔ رنگ‌ها</option>
              {colors.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          {materials.length > 0 && (
            <label className="field">
              جنس
              <select
                value={f.material}
                onChange={(e) => set("material", e.target.value)}
              >
                <option value="">همهٔ مواد</option>
                {materials.map((material) => <option key={material} value={material}>{material}</option>)}
              </select>
            </label>
          )}
          {!catalog.demo && highestPrice > 0 && (
            <fieldset>
              <legend>بازهٔ قیمت ({currency.label})</legend>
              <div className="price-slider-labels"><span>از {number(selectedMin)} {currency.label}</span><strong>تا {number(selectedMax)} {currency.label}</strong></div>
              <div className="price-slider price-slider-dual" style={{ "--price-start": `${highestPrice ? selectedMin / highestPrice * 100 : 0}%`, "--price-end": `${highestPrice ? selectedMax / highestPrice * 100 : 100}%` } as CSSProperties}>
                <input
                  aria-label="کمترین قیمت"
                  type="range"
                  min="0"
                  max={highestPrice}
                  step={1000}
                  value={selectedMin}
                  onChange={(e) => set("min", Number(e.target.value) <= 0 ? "" : String(Math.min(Number(e.target.value), selectedMax)))}
                />
                <input
                  aria-label="بیشترین قیمت"
                  type="range"
                  min="0"
                  max={highestPrice}
                  step={1}
                  value={selectedMax}
                  onChange={(e) => set("max", Number(e.target.value) >= highestPrice ? "" : String(Math.max(Number(e.target.value), selectedMin)))}
                />
              </div>
            </fieldset>
          )}
          {!catalog.demo && (
            <label className="field">
              وضعیت
              <select
                value={f.availability}
                onChange={(e) => set("availability", e.target.value)}
              >
                <option value="">همه</option>
                <option value="ready">آماده</option>
                <option value="order">ساخت سفارشی</option>
                <option value="unavailable">ناموجود</option>
              </select>
            </label>
          )}
          <button
            className="text-link"
            onClick={() => {
              setF(empty);
              setLimit(6);
            }}
          >
            پاک کردن همهٔ فیلترها <X size={15} />
          </button>
        </aside>
        <div className="catalog-results">
          <div className="filter-chips">
            {Object.entries(f)
              .filter(([k, v]) => k !== "sort" && v)
              .map(([k, v]) => (
                <button key={k} onClick={() => set(k as keyof Filters, "")}>
                  {labels[k]}: {display(k, v)} <X size={14} />
                  <span className="sr-only">حذف فیلتر</span>
                </button>
              ))}
          </div>
          {products.length ? (
            <>
              <div className="product-grid catalog-grid">
                {products.slice(0, limit).map((p) => (
                  <ProductCard key={p.id} product={productCardData(p)} />
                ))}
              </div>
              {limit < products.length && (
                <button
                  className="button outline load-more"
                  onClick={() => setLimit(limit + 6)}
                >
                  نمایش محصولات بیشتر ({number(products.length - limit)})
                </button>
              )}
            </>
          ) : (
            <div className="empty-state">
              <Search size={40} />
              <h2>{collection ? "فعلاً محصول شگفت‌انگیزی پیدا نشد." : "چیزی با این مشخصات پیدا نشد."}</h2>
              <p>{collection ? "با ثبت تخفیف برای محصولات، آن‌ها در این صفحه نمایش داده می‌شوند." : "فیلترها را تغییر دهید یا عبارت دیگری جست‌وجو کنید."}</p>
              <button className="button primary" onClick={() => setF(empty)}>
                {collection ? "پاک کردن فیلترها" : "نمایش همهٔ محصولات"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
